import deepMerge from "deepmerge";
import { isNil, isString } from "lodash";

import { notify } from "@cayuse/shared-react.components.banner-notification";
import { isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { notifyError } from "utils/errorNotification";
import { decodeURIComponentSafe } from 'utils/decodeURI';
import { getStore } from "store/store";
import {
	ERROR_NOT_AUTHORIZED,
	ERROR_STATUS_CODE_400,
	ERROR_STATUS_CODE_401,
	ERROR_STATUS_CODE_403,
	ERROR_STATUS_CODE_500,
	ERROR_UNABLE_DOWNLOAD_FILE,
	ERROR_INTERNAL_SERVER_ERROR,
	PROD_ENV,
	ERROR_FORBIDDEN_FILE_EXTENSION,
} from "globals/constants";

const DEFAULT_NUM_RETRIES = 2;

export class CayuseError extends Error {
	constructor(message, httpResponseObject = null) {
		super(message);

		this.name = "CayuseError";
		this.message = message;
		this.cause = httpResponseObject;
		this.response = httpResponseObject;
		this.meta = {
			// request ID from the SP service
			spRequestId: httpResponseObject?.headers?.get("x-sp-request-id") ?? "",
			// request ID from the flowable design service
			flowableDesignRequestId: httpResponseObject?.headers?.get("x-flowable-design-request-id") ?? "",
			// request ID from the flowable engage service
			flowableEngageRequestId: httpResponseObject?.headers?.get("x-flowable-engage-request-id") ?? "",
		};
	}
}

export const getTokenSilently = () => {
	return getStore().then(store => {
		//todo: move to own redux spot
		const token = store.getState().shell?.cayuseHeader?.authenticationToken;
		if (token) {
			return Promise.resolve(token);
		} else {
			return Promise.reject(new CayuseError("No token"));
		}
	});
};

export const checkErrorBody = async (error) => {
	const contentType = error.headers.get("Content-Type");

	if (contentType && contentType.includes("json")) {
		const errorBody = await error.json().catch(() => ({})) ?? {};

		if (errorBody.error === ERROR_FORBIDDEN_FILE_EXTENSION) {
			notifyError(errorBody.message, new CayuseError(error.statusText, error));
		}

		if(errorBody.message) {
			notify(errorBody.message, error);
		}

	} else if (contentType && contentType.includes("text/plain")) {
		const text = await error.text();
		notifyError(text, new CayuseError(error.statusText, error));
	} else {
		notifyError(ERROR_NOT_AUTHORIZED, new CayuseError(ERROR_NOT_AUTHORIZED, error));
	}
};

const checkResponseStatus = async (response, notifyUponError) => {
	if (response.status === ERROR_STATUS_CODE_401 && notifyUponError) {
		notify("", { type: "login" });
	} else if ((response.status === ERROR_STATUS_CODE_403 || response.status === ERROR_STATUS_CODE_400) && notifyUponError) { // TODO inject 400 bad request message from BE
		await checkErrorBody(response);
	} else if (response.status === ERROR_STATUS_CODE_500) {
		const err = new CayuseError(ERROR_INTERNAL_SERVER_ERROR, response);

		if (notifyUponError) {
			notifyError(ERROR_INTERNAL_SERVER_ERROR, err);
		}
		throw err;
	}

	if (!response.ok) {
		throw new CayuseError(response.statusText, response);
	}

	return response;
};

const fetchWithRetry = (url, options, args = {}) => {
	const { numRetries, attemptNumber, notifyUponError } = {
		...{
			numRetries: DEFAULT_NUM_RETRIES,
			attemptNumber: 1,
			notifyUponError: !PROD_ENV
		},
		...args
	};

	const retryTimeoutInSeconds = Math.pow(2, attemptNumber - 1);

	return fetch(url, options)
		.then(response => checkResponseStatus(response, notifyUponError))
		.then(response => {
			const contentType = response.headers.get("Content-Type");

			if (contentType && contentType.includes("json")) {
				// In cases where the response is empty,
				// we want to return an empty object instead of throwing an error
				return response.json().catch(() => ({}));
			} else {
				return response.text();
			}
		})
		.catch(error => {
			if (error.message === ERROR_INTERNAL_SERVER_ERROR) {
				if (numRetries === 0) {
					if (notifyUponError) {
						notifyError(error.message, error);
					}
					throw error;
				}

				return new Promise(resolve => {
					setTimeout(() => resolve(fetchWithRetry(url, options, {
						numRetries: numRetries - 1,
						attemptNumber: attemptNumber + 1,
						notifyUponError,
					})), retryTimeoutInSeconds * 1000);
				});
			} else {
				throw error;
			}
		});
};

export const request = async (url, args = {}) => {
	return executeRequest(url, {}, args);
};

export const save = async (url, payload, args = {}) => {
	const options = {
		method: "POST",
		body: JSON.stringify(payload),
		headers: {
			"Content-Type": "application/json"
		}
	};

	return executeRequest(url, options, args);
};

export const remove = async (url, args = {}) => {
	const options = {
		method: "DELETE",
		headers: {
			"Content-Type": "application/json"
		}
	};

	return executeRequest(url, options, args);
};

export const put = async (url, payload, args = {}) => {
	const options = {
		method: "PUT",
		body: JSON.stringify(payload),
		headers: {
			"Content-Type": "application/json"
		}
	};

	return executeRequest(url, options, args);
};

export const patch = async (url, payload, args = {}) => {
	const options = {
		method: "PATCH",
		body: JSON.stringify(payload),
		headers: {
			"Content-Type": "application/json"
		}
	};

	return executeRequest(url, options, args);
};

export const executeRequest = (url, options = {}, args = {}) => {
	const doFetch = token => {
		const allOptions = {
			...options,
			headers: {
				...(options.headers || {}),
				Authorization: `Bearer ${token}`,
				"X-IDP-New-Login": true
			}
		};

		if(options.method !== "GET" && !args.numRetries) {
			args.numRetries = 0;
		}

		return fetchWithRetry(url, allOptions, args);
	};

	return getTokenSilently().then(token => doFetch(token));
};

export const getCsvData = (url, requestParameters = {}, { notifyUponError } = { notifyUponError: true }) => {
	const download = token => {
		const defaultParameters = {
			headers: {
				Authorization: `Bearer ${token}`,
				"X-IDP-New-Login": true.toString()
			}
		};

		return fetch(url, deepMerge(defaultParameters,requestParameters))
			.then(response => {
				if (!response.ok) {
					if (notifyUponError) {
						throw new CayuseError(ERROR_UNABLE_DOWNLOAD_FILE, response);
					} else {
						throw new CayuseError(response.statusText, response);
					}
				}

				return response.blob();
			});
	};

	return getTokenSilently()
		.then(token => download(token))
		.catch(error => {
			if (notifyUponError) {
				notifyError(error.message || "Unable to fetch token", error);
			}

			throw error;
		});
};

export const downloadFile = (
	url,
	fileName,
	requestParameters = {},
	{ notifyUponError } = { notifyUponError: true },
) => {
	return getCsvData(url, requestParameters, { notifyUponError })
		.then(blob => {
			const anchor = document.createElement("a");
			const objectUrl = window.URL.createObjectURL(blob);
			document.body.appendChild(anchor);

			anchor.href = objectUrl;
			anchor.download = fileName;
			anchor.click();

			window.URL.revokeObjectURL(objectUrl);
		})
		.catch(error => {
			if (!notifyUponError) {
				throw error;
			}

			const message = isNonEmptyString(error.message)
				? error.message
				: null;

			notifyError(message, error);
		});
};

export const uploadFile = async (url, payload, args = {}) => {
	const formData = new FormData();
	for(const property in payload) {
		formData.append(property, payload[property]);
	}

	const options = {
		method: "POST",
		body: formData,
	};

	return executeRequest(url, options, args);
};

export const buildSearchParams = params => {
	let string = '';

	if (isNonEmptyObject(params)) {
		let instance = new URLSearchParams();

		for (const [ key, value ] of Object.entries(params)) {
			const notEmptyString = isString(value) && isNonEmptyString(value);

			// set param to instance when value is valid
			if (!isNil(value) || notEmptyString) {
				instance.append(key, decodeURIComponentSafe(value));
			}
		}

		// check is final search param string is not empty
		if (instance.toString().length > 0) {
			string = `?${instance.toString()}`;
		}
	}

	return string;
};

export const parseHttpErrorBody = async error => {
	const contentType = error?.headers?.get("Content-Type");
	let errorBody = error;

	if (contentType && contentType.includes("json")) {
		errorBody = await error.json().catch(() => ({})) ?? {};
	}

	return errorBody;
};

const throwIfFalse = (condition, message) => {
    if (!condition) {
        throw new Error(message);
    }
};

const callIfDefined = (fn, ...args) => {
    if (typeof fn === 'function') {
        fn(...args);
    }
};

export const downloadMultipartFile = async (options) => {
    const { id, fileName } = options;

    try {
		const downloadUrl = await request(`/api/v2/attachments/download?uploadMetadataId=${encodeURIComponent(id)}`);

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;
        link.click();
    }
    catch (e) {
        console.error(e);
        throw e;
    }
};

export const createMultipartUpload = (cb) => {
    const onEventHandler = (...args) => {
        if (typeof cb === 'function') {
            cb(...args);
        }
    };

    const createState = (initialState) => {
        const state = { ...initialState };

        const setState = (newState) => {
            Object.assign(state, newState);
            onEventHandler(state);
        };

        return [state, setState];
    };

    const [state, setState] = createState({ status: 'ready' });
    const controller = new AbortController();

    const uploadFile = async (file) => {
        throwIfFalse(file instanceof File, 'file must be a File object');
        throwIfFalse(file.name.length > 0, 'name must not be empty');
        throwIfFalse(file.size > 0, 'data must not be empty');

        const numberOfParts = Math.ceil(file.size / (8 * 2 ** 20));
        throwIfFalse(numberOfParts > 0, 'numberOfParts must be greater than 0');

		const bearerToken = await getTokenSilently();
        const response = await fetch('/api/v2/attachments/multipart/upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
				'Authorization': `Bearer ${bearerToken}`
            },
            body: JSON.stringify({
                fileName: file.name,
                fileSize: file.size,
                numberOfParts: numberOfParts,
            }),
            signal: controller.signal
        });

        const uploadData = await response.json();

        const uploadUrls = uploadData.uploadUrls;
        throwIfFalse(Array.isArray(uploadUrls), 'uploadUrls must be an array');
        throwIfFalse(uploadUrls.length > 0, 'uploadUrls must not be empty');

        setState({
            fileId: uploadData.uploadMetadataId,
            uploadId: uploadData.uploadId,
            uploadUrls,
            fileObject: file
        });

        console.log(uploadData);
        const partSize = Math.ceil(file.size / numberOfParts);

        setState({ status: 'uploading', progress: 0 });

        const finalizeUpload = async () => {
            // finalizing upload does not make the file permanently available
            // The file only becomes permanently available after the upload is attached to a record
            // and made permanent using the PATCH /api/v2/upload?uploadMetadataId=... endpoint

            setState({ status: 'finalizing' });

			const bearerToken = await getTokenSilently();
            const response = await fetch(`/api/v2/attachments/multipart/upload/${state.fileId}/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${bearerToken}`
                },
                body: JSON.stringify({
                    uploadId: state.uploadId,
                    etags: state.uploaded,
                }),
                signal: controller.signal
            });

            const fileMetadata = await response.json();
            setState({ status: 'done', progress: 100 });
			return fileMetadata;
        };

        const preparePart = async (partNumber) => {
            const start = (partNumber - 1) * partSize;
            const end = Math.min(start + partSize, file.size);
            const partData = file.slice(start, end);
            const uploadUrl = uploadUrls[partNumber - 1];

            const response = await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Length': partData.size
                },
                body: partData,
                signal: controller.signal
            });
            const eTag = await response.headers.get('ETag');

            const part = {
                partNumber,
                "etagId": JSON.parse(eTag)
            };

            const uploaded = state.uploaded || [];
            uploaded.push(part);

            uploaded.sort((a, b) => a.partNumber - b.partNumber);

            setState({
                uploaded,
                progress: Math.round((uploaded.length / numberOfParts) * 100)
            })

            return part;
        };

        const parts = [];
        for (let i = 1; i <= numberOfParts; i++) {
            parts.push(preparePart(i));
        }

        const partsResult = await Promise.all(parts);

        const uploadFileData = {
            uploadId: state.uploadId,
            etags: partsResult
        };

        const fileMetadata = await finalizeUpload(uploadFileData);
		return fileMetadata;
    };


    return {
        upload: async (file) => {
            try {
                throwIfFalse(state.status === 'ready', 'upload called on an upload that has already started');
                throwIfFalse(file instanceof File, 'file must be a File object');
                throwIfFalse(file.size > 0, 'file must not be empty');
                throwIfFalse(file.name.length > 0, 'file must have a name');

                setState({ status: 'preparing' });
                return await uploadFile(file);
            }
            catch (e) {
                setState({ status: 'error', error: e });
				throw e;
            }
        },

        cancel: async () => {
            if (state.status === 'ready') {
                return;
            }

            try {
                controller.abort();
            }
            catch (e) {
                setState({ status: 'error', error: e });
				throw e;
            }
        }
    };
};

export default {
	request,
	save,
	put,
	patch,
	executeRequest,
	uploadFile,
	downloadFile,
	buildSearchParams,
	createMultipartUpload
};
